import { History } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/i18n'
import { HISTORICAL_PAYMENT_TYPES, type HistoricalPaymentInput, useCreateHistoricalPayment } from '../../api'
import { Field, ModalFooter } from './Field'

// Yerel tarih (UTC'ye çevirmek gece yarısına yakın saatlerde günü kaydırır).
const today = () => new Date().toLocaleDateString('sv-SE')
const thisMonth = () => today().slice(0, 7)

/**
 * Sistem öncesi dönemden ödenmiş personel ödemesi (maaş, avans, yemek, ulaşım, prim, masraf). Ödenmiş ve onaylı
 * olarak kaydedilir; onay ve banka ödeme akışına girmez. Arka arkaya birden fazla kayıt girilebilsin diye form
 * kaydettikten sonra açık kalır.
 */
export function GecmisOdemeModal({ personelId, onClose }: { personelId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const create = useCreateHistoricalPayment()
  const [form, setForm] = useState<Omit<HistoricalPaymentInput, 'employeeId'>>({
    paymentType: 'SALARY',
    amount: '',
    period: '',
    paymentDate: '',
    description: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }))

  const errPeriod = submitted && (!form.period || form.period > thisMonth()) ? t('hr.personel.gecmisOdeme.periodPast') : undefined
  const errDate = submitted && (!form.paymentDate || form.paymentDate > today()) ? t('hr.personel.gecmisOdeme.datePast') : undefined
  const errAmount = submitted && !(Number(form.amount) > 0) ? t('hr.personel.actions.required') : undefined

  const submit = () => {
    setSubmitted(true)
    if (!form.period || form.period > thisMonth() || !form.paymentDate || form.paymentDate > today() || !(Number(form.amount) > 0)) return
    create.mutate(
      { employeeId: personelId, ...form },
      {
        onSuccess: () => {
          toast.success(t('hr.personel.gecmisOdeme.saved', { type: t(`hr.personel.gecmisOdeme.types.${form.paymentType}`), period: form.period }))
          // Aynı türden bir sonraki ayı girmek kolay olsun: tutar ve tür korunur, dönem/tarih boşaltılır.
          setForm((f) => ({ ...f, period: '', paymentDate: '', description: '' }))
          setSubmitted(false)
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      icon={<History />}
      title={t('hr.personel.gecmisOdeme.title')}
      description={t('hr.personel.gecmisOdeme.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={<ModalFooter onCancel={onClose} submitLabel={t('hr.personel.gecmisOdeme.save')} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="go-tur" label={t('hr.personel.gecmisOdeme.type')}>
          <Select
            id="go-tur"
            value={form.paymentType}
            onChange={(v) => set('paymentType', v)}
            options={HISTORICAL_PAYMENT_TYPES.map((x) => ({ value: x, label: t(`hr.personel.gecmisOdeme.types.${x}`) }))}
          />
        </Field>
        <Field id="go-tutar" label={t('hr.personel.gecmisOdeme.amount')} error={errAmount}>
          <NumberInput id="go-tutar" className="h-10 rounded-xl" value={form.amount} onChange={(v) => set('amount', v)} maxDecimals={2} minDecimalsOnBlur={2} suffix="TRY" aria-invalid={!!errAmount} />
        </Field>
        <Field id="go-donem" label={t('hr.personel.gecmisOdeme.period')} error={errPeriod}>
          <Input id="go-donem" type="month" className="h-10 rounded-xl" value={form.period} max={thisMonth()} onChange={(e) => set('period', e.target.value)} aria-invalid={!!errPeriod} />
        </Field>
        <Field id="go-tarih" label={t('hr.personel.gecmisOdeme.date')} error={errDate}>
          <DateField id="go-tarih" value={form.paymentDate} max={today()} onChange={(v) => set('paymentDate', v)} aria-invalid={!!errDate} />
        </Field>
        <Field id="go-aciklama" label={t('hr.personel.gecmisOdeme.description')} className="sm:col-span-2">
          <Textarea id="go-aciklama" rows={2} maxLength={500} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
